import Button from "components/shared/button";
import { Popup } from "components/popups/Popup";
import React, { useEffect, useState } from "react";
import Input from "components/shared/inputs/input";
import useTranslation from "utils/hooks/useTranslation";
import useCustomForm from "utils/hooks/useCustomForm";
import { useCreateAnnexMutation, useUpdateAnnexMutation } from "store/api";
import type { DatasourceMutationRequest } from "./TvaPopup";

type EditableAnnexRow = {
  id?: number | string;
  annex1?: string;
  annex2?: string;
  annex3?: string;
  annex4?: string;
  annex5?: string;
};

type AnnexPopupProps = {
  isOpen: boolean;
  onClose: () => void;
  request: DatasourceMutationRequest | null;
  envNumeric: number | undefined;
  mode?: "create" | "edit";
  row?: EditableAnnexRow | null;
  onSaved?: () => Promise<void> | void;
};

const AnnexPopup: React.FC<AnnexPopupProps> = ({
  isOpen,
  onClose,
  request,
  envNumeric,
  mode = "edit",
  row,
  onSaved,
}) => {
  const { t } = useTranslation();
  const [isSaving, setIsSaving] = useState(false);
  const [createAnnex] = useCreateAnnexMutation();
  const [updateAnnex] = useUpdateAnnexMutation();
  const isEditMode = mode === "edit";

  const { control, reset, getValues } = useCustomForm<{
    annex1: string;
    annex2: string;
    annex3: string;
    annex4: string;
    annex5: string;
  }>({
    defaultValues: {
      annex1: "",
      annex2: "",
      annex3: "",
      annex4: "",
      annex5: "",
    },
  });

  useEffect(() => {
    setIsSaving(false);
    reset({
      annex1: isOpen ? String(row?.annex1 ?? "") : "",
      annex2: isOpen ? String(row?.annex2 ?? "") : "",
      annex3: isOpen ? String(row?.annex3 ?? "") : "",
      annex4: isOpen ? String(row?.annex4 ?? "") : "",
      annex5: isOpen ? String(row?.annex5 ?? "") : "",
    });
  }, [isOpen, reset, row?.annex1, row?.annex2, row?.annex3, row?.annex4, row?.annex5]);

  const handleSave = async () => {
    if (!request) return;
    const rowId = row?.id ?? (row as any)?.key;
    if (isEditMode && rowId == null) return;
    if (!isEditMode && (envNumeric == null || Number.isNaN(envNumeric))) return;

    const { annex1, annex2, annex3, annex4, annex5 } = getValues();
    const baseUrl = request.url.replace(/\/[^/]+$/, "");
    const url = isEditMode ? `${baseUrl}/${rowId}` : baseUrl;

    const payload = {
      id: rowId,
      annex1,
      annex2,
      annex3,
      annex4,
      annex5,
      ...(isEditMode ? {} : { env: envNumeric }),
    };

    setIsSaving(true);
    try {
      if (isEditMode) {
        await updateAnnex({ url, data: payload }).unwrap();
      } else {
        await createAnnex({ url, data: payload }).unwrap();
      }

      onSaved?.();
      onClose();
    } catch {
      // handled by api interceptor
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Popup
      title={isEditMode ? "Modifier annexe" : "Nouvelle annexe"}
      onClose={onClose}
      portal
      fixSize="middle"
      closeOnOutsideClick
      commandPanelProps={{
        additionalElement: (
          <group data-justify="right" data-gap="10">
            <Button type="button" onClick={onClose} disabled={isSaving}>
              {t("Back")}
            </Button>
            <Button type="button" primary onClick={handleSave} disabled={isSaving}>
              {t(isEditMode ? "Update" : "Add")}
            </Button>
          </group>
        ),
      }}
    >
      <group
        data-direction="column"
        data-gap="20"
        data-space="40"
        data-space-horizontal="40"
        data-space-top="5"
        data-height="autofit"
      >
        <group data-gap="10" data-space-vertical="20">
          <group data-direction="column" data-gap="20">
            <Input dataLength="autofit" label={"Annex 1"} name="annex1" control={control} type="textarea" size="large" />
            <Input dataLength="autofit" label={"Annex 2"} name="annex2" control={control} type="textarea" size="large" />
            <Input dataLength="autofit" label={"Annex 3"} name="annex3" control={control} type="textarea" size="large" />
            <Input dataLength="autofit" label={"Annex 4"} name="annex4" control={control} type="textarea" size="large" />
            <Input dataLength="autofit" label={"Annex 5"} name="annex5" control={control} type="textarea" size="large" />
          </group>
        </group>
      </group>
    </Popup>
  );
};

export default AnnexPopup;
